import React, { useState } from "react";
import { Input, Label, Text, Button, Dropdown, Option, makeStyles } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import SearchResourceInGraph from "../../GenericComponent/SearchResourceInGraph";
import QueueSelectorComponent from "../../GenericComponent/QueueSelectorComponent";
import "./CSS/RegexpComponent.css";

const useStyles = makeStyles({
  container: { display: "flex", flexDirection: "column", gap: "1rem" },
  row: { display: "flex", gap: "0.5rem", alignItems: "center" },
  subRow: { display: "flex", flexDirection: "column", gap: "0.5rem" },
});

export default function RegexpComponent({
  data,
  routingName,
  onChange,
  setOnMatched,
  setOnUnMatched,
  routingList,
  permissions,
  isEditMode
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [testInput, setTestInput] = useState("");
  const [message, setMessage] = useState("");

  const REGEX_NEXT_ACTIONS = [
    { value: "queue", textKey: "queue" },
    { value: "transferUser", textKey: "transferToUser" },
    { value: "disconnect", textKey: "disconnect" },
    { value: "routing", textKey: "routing" },
    { value: "transferPhone", textKey: "transferToPhone" },
  ];

  const getTranslatedValue = (type, targetType) => {
    let value = type;
    if (type === "transfer") {
      value = targetType === "phone" ? "transferPhone" : "transferUser";
    }
    return t(REGEX_NEXT_ACTIONS.find(a => a.value === value)?.textKey || value);
  };

  const handleTestRegex = () => {
    try {
      const regex = new RegExp(data.regExp || "");
      setMessage(regex.test(testInput) ? t("matched") : t("notMatched"));
    } catch {
      setMessage(t("invalidRegex"));
    }
  };

  return (
    <div className={classes.container}>
      <Label>{t("regExpPattern")}</Label>
      <Input
        className="regexp-input"
        placeholder={t("regExpExample")}
        value={data?.regExp || ""}
        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
        onChange={(_, val) => onChange(routingName, "regExp", val.value)}
      />


      <div className={classes.subRow}>
        <Label>{t("ifMatched")}</Label>
        <Dropdown
          className="regexp-dropdown"
          value={getTranslatedValue(data.onMatched?.type, data.onMatched?.targetType)}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={(_, dropdownData) => {
            const value = dropdownData.optionValue;
            let newData = {};
            
            if (value === "transferUser") {
              newData = { type: "transfer", targetType: "user" };
            } else if (value === "transferPhone") {
              newData = { type: "transfer", targetType: "phone" };
            } else if (value === "disconnect") {
              newData = { type: "disconnect" };
            } else {
              newData = { type: value };
            }
            
            setOnMatched(newData);
          }}
        >
          {REGEX_NEXT_ACTIONS.map(action => (
            <Option key={action.value} value={action.value} text={t(action.textKey)}>{t(action.textKey)}</Option>
          ))}
        </Dropdown>
        
        {/* Render components directly based on data */}
        {data.onMatched?.type === "queue" && (
          <QueueSelectorComponent
            displayName={data.onMatched?.queueDisplayName || ""}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            getData={(queue) =>
              setOnMatched({
                type: "queue",
                queueDisplayName: queue.displayName,
                queueId: queue.id
              })
            }
          />
        )}
        
        {data.onMatched?.type === "transfer" && data.onMatched?.targetType === "user" && (
          <SearchResourceInGraph
            displayName={data.onMatched?.userDisplayName || ""}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onSelectUser={(user) =>
              setOnMatched({
                type: "transfer",
                userDisplayName: user.displayName,
                targetId: user.id,
                targetType: "user"
              })
            }
          />
        )}
        
        {data.onMatched?.type === "transfer" && data.onMatched?.targetType === "phone" && (
          <Input
            className="regexp-input"
            value={data.onMatched?.targetId || ""}
            placeholder={t("phoneNumber")}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={(_, val) =>
              setOnMatched({
                type: "transfer",
                targetId: val.value,
                targetType: "phone"
              })
            }
          />
        )}
        
        {data.onMatched?.type === "routing" && (
          <Dropdown
            value={data.onMatched?.routingName === "0" ? t("start") : data.onMatched?.routingName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onOptionSelect={(_, dropdownData) =>
              setOnMatched({
                type: "routing",
                routingName: dropdownData.optionValue
              })
            }
          >
            {Object.keys(routingList || {}).map(rn => {
              const displayName = rn === "0" ? t("start") : rn;
              return <Option key={rn} value={rn}>{displayName}</Option>;
            })}
          </Dropdown>
        )}
      </div>

      <div className={classes.subRow}>
        <Label>{t("ifNotMatched")}</Label>
        <Dropdown
          className="regexp-dropdown"
          value={getTranslatedValue(data.onUnMatched?.type, data.onUnMatched?.targetType)}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={(_, dropdownData) => {
            const value = dropdownData.optionValue;
            let newData = {};
            
            if (value === "transferUser") {
              newData = { type: "transfer", targetType: "user" };
            } else if (value === "transferPhone") {
              newData = { type: "transfer", targetType: "phone" };
            } else if (value === "disconnect") {
              newData = { type: "disconnect" };
            } else {
              newData = { type: value };
            }
            
            setOnUnMatched(newData);
          }}
        >
          {REGEX_NEXT_ACTIONS.map(action => (
            <Option key={action.value} value={action.value} text={t(action.textKey)}>{t(action.textKey)}</Option>
          ))}
        </Dropdown>
        
        {/* Render components directly based on data */}
        {data.onUnMatched?.type === "queue" && (
          <QueueSelectorComponent
            displayName={data.onUnMatched?.queueDisplayName || ""}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            getData={(queue) =>
              setOnUnMatched({
                type: "queue",
                queueDisplayName: queue.displayName,
                queueId: queue.id
              })
            }
          />
        )}
        
        {data.onUnMatched?.type === "transfer" && data.onUnMatched?.targetType === "user" && (
          <SearchResourceInGraph
            displayName={data.onUnMatched?.userDisplayName || ""}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onSelectUser={(user) =>
              setOnUnMatched({
                type: "transfer",
                userDisplayName: user.displayName,
                targetId: user.id,
                targetType: "user"
              })
            }
          />
        )}
        
        {data.onUnMatched?.type === "transfer" && data.onUnMatched?.targetType === "phone" && (
          <Input
            className="regexp-input"
            value={data.onUnMatched?.targetId || ""}
            placeholder={t("phoneNumber")}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={(_, val) =>
              setOnUnMatched({
                type: "transfer",
                targetId: val.value,
                targetType: "phone"
              })
            }
          />
        )}
        
        {data.onUnMatched?.type === "routing" && (
          <Dropdown
            value={data.onUnMatched?.routingName === "0" ? t("start") : data.onUnMatched?.routingName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onOptionSelect={(_, dropdownData) =>
              setOnUnMatched({
                type: "routing",
                routingName: dropdownData.optionValue
              })
            }
          >
            {Object.keys(routingList || {}).map(rn => {
              const displayName = rn === "0" ? t("start") : rn;
              return <Option key={rn} value={rn}>{displayName}</Option>;
            })}
          </Dropdown>
        )}
      </div>

      <div className={classes.row}>
        <Input
          className="regexp-input"
          placeholder={t("testValue")}
          value={testInput}
          onChange={(_, val) => setTestInput(val.value)}
        />
        <Button appearance="primary" onClick={handleTestRegex}>
          {t("test")}
        </Button>
        {message && <Text style={{ marginLeft: '0.5rem', alignSelf: 'center' }}>{message}</Text>}
      </div>
    </div>
  );
}
